import React, { useEffect, useState } from 'react';
import { Form, message } from 'antd';
import { StyledDrawer, StyledDrawerTitle, StyledFooter } from './style';
import { Button, TextArea } from 'ui';
import { Label } from 'ui/Label';
import { useIntl } from 'react-intl';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import EnumSelect from '../../../admin/Selects/EnumSelect';

interface CancelTransactionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  transactionId: number | string;
  transactionType?: string | null;
  isCustomer: boolean;
  onSuccess?: () => void;
}

const CancelTransactionDrawer = ({
  isOpen,
  onClose,
  transactionId,
  transactionType,
  isCustomer,
  onSuccess,
}: CancelTransactionDrawerProps) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDescriptionField, setShowDescriptionField] = useState(false);

  const isWholesaleTransaction = [
    'purchase',
    'reservation',
    'dynamic_purchase',
  ].includes(transactionType?.toLowerCase() || '');

  const getAvailableCancelReasons = () => {
    if (isWholesaleTransaction && isCustomer) {
      return [
        'cancelled_not_actual',
        'cancelled_unexpected',
        'cancelled_transferred',
        'cancelled_exceeded_deadline',
        'cancelled_quality',
        'cancelled_service',
        'cancelled_other',
      ];
    }

    if (isWholesaleTransaction) {
      return [
        'cancelled_not_actual',
        'cancelled_unexpected',
        'cancelled_cant_provide_qty',
        'cancelled_transferred',
        'cancelled_exceeded_deadline',
        'cancelled_quality',
        'cancelled_service',
        'cancelled_other',
      ];
    }

    if (isCustomer) {
      return [
        'cancelled_not_actual',
        'cancelled_unexpected',
        'cancelled_other',
      ];
    }

    return [
      'cancelled_not_actual',
      'cancelled_cant_provide_qty',
      'cancelled_unexpected',
      'cancelled_other',
    ];
  };

  const { appendData: cancelTransaction } = useQueryApiClient({
    request: {
      url: 'api/v2/transactions/change-status',
      method: 'POST',
    },
    onSuccess: () => {
      message.success(
        intl.formatMessage({ id: 'general.transaction_cancelled_successfully' })
      );
      setIsSubmitting(false);
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: () => {
      message.error(intl.formatMessage({ id: 'general.error_occurred' }));
      setIsSubmitting(false);
    },
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      cancelTransaction({
        id: transactionId,
        status: 'canceled',
        cancel_reason: values.cancel_reason,
        cancel_reason_description:
          values.cancel_reason === 'cancelled_other'
            ? values.cancel_reason_description
            : null,
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancelReasonChange = (value: string) => {
    setShowDescriptionField(value === 'cancelled_other');
    if (value !== 'cancelled_other') {
      form.setFieldsValue({ cancel_reason_description: null });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
      setShowDescriptionField(false);
    }
  }, [isOpen, form]);

  const footer = (
    <StyledFooter>
      <Button
        type="default"
        label={intl.formatMessage({ id: 'general.cancel_transaction_back' })}
        onClick={onClose}
      />
      <Button
        type="primary"
        label={intl.formatMessage({
          id: isCustomer ? 'general.cancel_order' : 'general.cancel_transaction',
        })}
        onClick={handleSubmit}
        loading={isSubmitting}
      />
    </StyledFooter>
  );

  return (
    <StyledDrawer
      width={600}
      open={isOpen}
      onClose={onClose}
      className="styled-drawer"
      title={
        <StyledDrawerTitle>
          <Label
            label={intl.formatMessage({
              id: isCustomer ? 'general.cancel_order' : 'general.cancel_transaction',
            })}
            title={true}
          />
        </StyledDrawerTitle>
      }
      footer={footer}
      destroyOnClose
      zIndex={1300}
    >
      <Form form={form} layout="vertical">
        <EnumSelect
          name="cancel_reason"
          label={intl.formatMessage({ id: 'general.cancel_reason' })}
          code="OrderTransactionCancelType"
          limited={getAvailableCancelReasons()}
          validations={['required']}
          onChange={handleCancelReasonChange}
        />

        {showDescriptionField && (
          <TextArea
            name="cancel_reason_description"
            validations={['required']}
            maxLength={255}
          />
        )}
      </Form>
    </StyledDrawer>
  );
};

export default CancelTransactionDrawer;
